import React from 'react';
import AuthCard from '@/components/AuthCard'
import AuthValidationErrors from '@/components/AuthValidationErrors'
import Input from '@/components/Input'
import Label from '@/components/Label'
import Link from 'next/link'
import { useAuth } from '@/hooks/auth'
import { useState, useEffect } from 'react'
import { useLoadScript } from "@react-google-maps/api"
import { GOOGLE_MAPS_LIBRARIES } from '@/lib/googleMapsLibraries'
import PlacesAutocomplete from '@/components/PlacesAutocomplete'
import axios from '@/lib/axios'
import { fetchMerchantCategoriesList } from '@/lib/merchant-categories-public'
import { useI18n } from '@/lib/I18nProvider'
import { isValidNorthAmericanPhone } from '@/lib/utils'

const TERMS_URL_BY_LOCALE = {
    en: 'https://www.onship.ca/en/terms-conditions',
    fr: 'https://www.onship.ca/conditions-utilisation',
}

function getMerchantCategoryLabel(category, t, locale) {
    const name = category?.name ?? ''
    const id = category?.id
    if (id == null || id === '') return name
    if (locale !== 'en') return name
    const key = `merchants.merchantCategoryLabels.${id}`
    const translated = t(key)
    return translated !== key ? translated : name
}

const MerchantRegisterForm = () => {
    const { t, locale } = useI18n();
    const termsUrl = TERMS_URL_BY_LOCALE[locale] ?? TERMS_URL_BY_LOCALE.fr
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const { register } = useAuth({
        middleware: 'guest',
        redirectIfAuthenticated: '/dashboard',
    })

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordConfirmation, setPasswordConfirmation] = useState('')
    const [address, setAddress] = useState('')
    const [postalCode, setPostalCode] = useState('')
    const [latitude, setLatitude] = useState('')
    const [longitude, setLongitude] = useState('')
    const [phone, setPhone] = useState('')
    const [city, setCity] = useState('')
    const [contactName, setContactName] = useState('')
    const [contactTel, setContactTel] = useState('')
    const [categories, setCategories] = useState([])
    const [merchantCategory, setMerchantCategory] = useState('');
    const [errors, setErrors] = useState([])
    const [success, setSuccess] = useState('')
    const [currentStep, setCurrentStep] = useState(1)
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
    const [hideAnswer, setHideAnswer] = useState(true);
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

    const onPlaceSelected = (destination, latLng, zipCode, meta) => {
        const parts = destination.split(',').map((s) => s.trim()).filter(Boolean);
        const street = parts[0] ?? destination.trim();
        const cityValue = (meta?.locality ?? parts[1] ?? '').trim();
        setAddress(street);
        setCity(cityValue);
        setPostalCode(zipCode);
        setLatitude(latLng.lat);
        setLongitude(latLng.lng);
    }

    const submitSuccess = () => {
        setCurrentStep(currentStep+1);
    }

    const submitForm = () => {

        register({ 
            name: name, 
            email: email, 
            password, 
            password_confirmation: passwordConfirmation, 
            MCATID: merchantCategory, 
            ADDRESS1: address, 
            POSTAL_CODE: postalCode, 
            CITY: city, 
            LAT: latitude, 
            LNG: longitude, 
            TEL1: phone, 
            CONTACT_NAME: contactName, 
            CONTACT_TEL: contactTel, 
            role: 'merchant', 
            setErrors, 
            submitSuccess,
            setIsLoadingSubmit
        })
    }

    const handleCategoryChange = (e) => {
        setMerchantCategory(e.target.value);
    }

    const previous = () => {
        if (currentStep>1){
            setCurrentStep(currentStep-1);
        }
    }

    const next = () => {
        if (currentStep == 1) {
            if (!name) {
                setErrors([t('merchants.validation.nameRequired')]);
                return;
            }
            if (!email) {
                setErrors([t('merchants.validation.emailRequired')]);
                return;
            }
            if (!password) {
                setErrors([t('merchants.validation.passwordRequired')]);
                return;
            }
            if (password.length < 8) {
                setErrors([t('merchants.validation.passwordMinLength')]);
                return;
            }
            if (!passwordConfirmation) {
                setErrors([t('merchants.validation.passwordConfirmationRequired')]);
                return;
            }
            if (password !== passwordConfirmation) {
                setErrors([t('merchants.validation.passwordsDoNotMatch')]);
                return;
            }
            setErrors([]);
            setCurrentStep(currentStep + 1);
        } else if (currentStep == 2) {
            if (!merchantCategory) {
                setErrors([t('merchants.validation.merchantTypeRequired')]);
                return;
            }
            if (!address) {
                setErrors([t('merchants.validation.addressRequired')]);
                return;
            }
            if (!phone) {
                setErrors([t('merchants.validation.phoneRequired')]);
                return;
            }
            if (!isValidNorthAmericanPhone(phone)) {
                setErrors([t('validation.invalidPhone')]);
                return;
            }
            if (!contactName) {
                setErrors([t('merchants.validation.contactNameRequired')]);
                return;
            }
            if (!contactTel) {
                setErrors([t('merchants.validation.contactPhoneRequired')]);
                return;
            }
            if (!isValidNorthAmericanPhone(contactTel)) {
                setErrors([t('validation.invalidPhone')]);
                return;
            }
            if (!hasAcceptedTerms) {
                setErrors([t('merchants.validation.termsRequired')]);
                return;
            }
            setErrors([]);
            submitForm();
        }
    }
    
    useEffect(() => {
        fetchMerchantCategoriesList().then(setCategories)
    }, []);
    
    return (
        <>
            
            <div className="steps" id="stepsDemo">
                <div className={`step-item ${currentStep == 1 ? "is-active is-success" : ""} ${currentStep >= 1 ? "is-completed" : ""}`}>
                    <div className="step-marker">1</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.account')}</p>
                    </div>
                </div>
                <div className={`step-item ${currentStep == 2 ? "is-active is-success" : ""} ${currentStep >= 2 ? "is-completed" : ""}`}>
                    <div className="step-marker">2</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.profile')}</p>
                    </div>
                </div>
                <div className={`step-item ${currentStep == 3 ? "is-active is-success" : ""} ${currentStep >= 3 ? "is-completed" : ""}`}>
                    <div className="step-marker">3</div>
                    <div className="step-details">
                        <p className="step-title">{t('merchants.registration.confirmation')}</p>
                    </div>
                </div>
                <div className="steps-content has-text-centered">
                    
                    <AuthCard>
                        {/* Validation Errors */}
                        <h1 className="title">{t('merchants.registration.title')}</h1>
                        {
                            success ? (
                                <p className="has-text-success">{success}<br/><br/></p>
                            ) : null
                        }
                        <AuthValidationErrors className="mb-4" errors={errors} />

                        <div className={`step-content ${currentStep == 1 ? "is-active" : ""}`}>
                            <div>
                                {/* Name */}
                                <div>
                                    <Label htmlFor="business-name">{t('merchants.form.generalInfo.businessName')}</Label>

                                    <Input
                                        id="business-name"
                                        type="text"
                                        value={name}
                                        className="block mt-1 w-full"
                                        onChange={event => setName(event.target.value)}
                                        required
                                        autoFocus
                                    />
                                </div>

                                {/* Email Address */}
                                <div className="mt-4">
                                    <Label htmlFor="email">{t('auth.email')}</Label>

                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        className="block mt-1 w-full"
                                        onChange={event => setEmail(event.target.value)}
                                        required
                                    />
                                </div>

                                {/* Password */}
                                <div className="mt-4">
                                    <Label htmlFor="password">{t('auth.password')}</Label>

                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        className="block mt-1 w-full"
                                        onChange={event => setPassword(event.target.value)}
                                        required
                                        autoComplete="new-password"
                                    />
                                    <p className="help">{t('merchants.validation.passwordMinLength')}</p>
                                </div>

                                {/* Confirm Password */}
                                <div className="mt-4">
                                    <Label htmlFor="passwordConfirmation">
                                        {t('merchants.validation.passwordConfirmationRequired')}
                                    </Label>

                                    <Input
                                        id="passwordConfirmation"
                                        type="password"
                                        value={passwordConfirmation}
                                        className="block mt-1 w-full"
                                        onChange={event =>
                                            setPasswordConfirmation(event.target.value)
                                        }
                                        required
                                    />
                                </div>

                            </div>
                        </div>
                        <div className={`step-content ${currentStep == 2 ? "is-active" : ""}`}>
                            <div>
                                {/* Name */}
                                <div>
                                    <Label htmlFor="address">{t('merchants.form.generalInfo.address')}</Label>
                                    {
                                        isLoaded ? (
                                            <PlacesAutocomplete setSelected={onPlaceSelected} parentValue={address} />
                                        ) : null
                                    }
                                </div>

                                {/* Email Address */}
                                <div className="mt-4">
                                    <Label htmlFor="phone">{t('merchants.form.generalInfo.phone')}</Label>

                                    <Input
                                        id="phone"
                                        type="phone"
                                        value={phone}
                                        className="block mt-1 w-full"
                                        onChange={event => setPhone(event.target.value)}
                                        required
                                    />
                                </div>

                                {/* Merchant Type */}
                                <div className="mt-4">
                                    <Label htmlFor="merchant-type">{t('merchants.form.generalInfo.merchantType')}</Label>

                                    <div className="select is-fullwidth">
                                        <select id="merchant-type" value={merchantCategory} onChange={handleCategoryChange}>
                                            <option value="">{t('merchants.form.generalInfo.merchantTypePlaceholder')}</option>
                                            {categories.map(category => (
                                                <option key={category.id} value={String(category.id)}>
                                                    {getMerchantCategoryLabel(category, t, locale)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>


                                {/* Password */}
                                <div className="mt-4">
                                    <Label htmlFor="contact-name">{t('merchants.form.generalInfo.contactPerson')}</Label>

                                    <Input
                                        id="contact-name"
                                        type="contact-name"
                                        value={contactName}
                                        className="block mt-1 w-full"
                                        onChange={event => setContactName(event.target.value)}
                                        required
                                    />
                                </div>

                                {/* Confirm Password */}
                                <div className="mt-4">
                                    <Label htmlFor="contact-tel">
                                        {t('merchants.form.generalInfo.contactPhone')}
                                    </Label>

                                    <Input
                                        id="contact-tel"
                                        type="contact-tel"
                                        value={contactTel}
                                        className="block mt-1 w-full"
                                        onChange={event =>
                                            setContactTel(event.target.value)
                                        }
                                        required
                                    />
                                </div>

                                <div className="mt-4 has-text-centered">
                                    <label className="checkbox" htmlFor="merchant-terms-accept">
                                        <input
                                            id="merchant-terms-accept"
                                            type="checkbox"
                                            checked={hasAcceptedTerms}
                                            onChange={event => setHasAcceptedTerms(event.target.checked)}
                                        />
                                        {' '}
                                        {t('merchants.registration.termsBeforeLink')}
                                        <a
                                            href={termsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="has-text-link"
                                        >
                                            {t('merchants.registration.termsLinkText')}
                                        </a>
                                        {t('merchants.registration.termsAfterLink')}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className={`step-content ${currentStep == 3 ? "is-active" : ""}`}>
                            <h1 className="title is-4 has-text-success">{t('merchants.registration.success')}</h1>
                        </div>
                        <br/>
                        <div className={`steps-actions ${currentStep == 3 ? "is-hidden" : ""}`}>
                            <div className={`steps-action ${currentStep == 1 ? "is-hidden" : ""}`}>
                                <button className="button" onClick={() => {previous()}}>{t('common.back')}</button>
                            </div>
                            <div className="steps-action">
                                <button className={`button is-primary ${isLoadingSubmit ? 'is-loading' : ''}`} onClick={() => {next()}}>{t('common.continue')}</button>
                            </div>
                        </div>
                    </AuthCard>
                        <div className="my-6">
                            <Link href="/login">
                                {t('merchants.registration.signInPrompt')}
                            </Link>
                        </div>
                </div>
            </div>
        </>
    )
}

export default MerchantRegisterForm
